import { formatTonnage, formatNumber } from '../../utils/formatting.js';
import {
  ALL_KILN_ANNUAL_FIELD_KEYS,
  ALL_EMISSION_FIELD_KEYS,
  ALL_KILN_LOAD_MASS_FLOW_KEYS,
  ALL_KILN_LOAD_OPERATING_KEYS,
  KILN_LOAD_COMPONENT_KEYS,
} from './environmental/kilnFields.js';
import type { AlternativeFuelEntry, RotaryKiln, RotaryKilnMeasurement } from '../../types/reports.js';

/**
 * Reine Abbildungsschicht Vorjahresmeldung -> Feldpfad/Vorwert-Text (24-RESEARCH.md § Frage 5).
 *
 * Kein React, kein Netz, kein Speicherzugriff -- die Funktionen hier nehmen ein bereits
 * hydriertes Formularobjekt (Ergebnis eines `hydrate*` aus `hydrate.ts`, angewandt auf die
 * VORJAHRESMELDUNG statt die laufende) entgegen und erzeugen genau die Punkt-Notation, die
 * `getPath` in `components/form/validatedFields.tsx:273-278` beim Lesen konsumiert. Der Beweis,
 * dass diese Punkt-Notation mit den `field=`-Props der Abschnitte uebereinstimmt, kommt
 * kostenlos aus der bereits getesteten Hydrierungsschicht (`hydrate.ts`) -- sie wird hier nur
 * genutzt, nicht verändert (`hydrate.ts` bleibt unangetastet).
 *
 * D-16 (Vorwerte nur an Zahlenfeldern) ist auf der LESESEITE gewahrt, nicht durch eine
 * handgepflegte Ausschlussliste: den Vorwert fragen nur `ValidatedNumberInput` und die
 * D-15-Anzeigestellen ab, nie `ValidatedTextInput`, `ValidatedTextarea` oder `ValidatedSelect`.
 * Die Filterregel unten wirft Freitext trotzdem strukturell heraus (siehe `isIncludableLeaf`) --
 * das haelt den Store klein und verhindert, dass ein Datumsstring oder ein HTML-Notizfeld als
 * Vorwert-Text landet, falls doch einmal ein Nicht-Zahlenfeld abgefragt wuerde. Ein LEERES
 * Textfeld kann dabei durchaus als '–' im Ergebnis landen (D-08 gilt feldunabhaengig) -- das ist
 * unschaedlich, weil ohnehin niemand danach fragt.
 */

/**
 * Wurzelnamen, die NIE einen Vorwert-Schluessel erzeugen -- samt allem darunter.
 *
 * Die freie Sonstige-Liste der Einsatzstoffe, `otherInputMaterials`, hat keine ueber Meldungen
 * hinweg tragfaehige Identitaet (freies Namensfeld plus Insert-/Sortierreihenfolge statt fester
 * Katalogposition oder Stammdaten-ID). Ein falsch zugeordneter Vorwert ist schlimmer als gar
 * keiner -- der Melder prueft dann gegen die Zahlen eines anderen Eintrags.
 *
 * `rotaryKilns` (Phase 28, D-15) ist SEIT PLAN 28-05 NICHT mehr hier gelistet: die Begruendung
 * "keine tragfaehige Identitaet" gilt fuer Drehoefen nicht mehr, seit sie Werks-Stammdaten sind
 * (`PlantKiln`) -- der Stammdaten-Ofen liefert eine feste ID, ueber die `remapKilnPositions`
 * unten die Vorjahresposition auf die aktuelle umsortiert. Die generische Flatten-Pipeline
 * durchlaeuft `rotaryKilns` deshalb zwar (u. a. fuer `measurements.*.id`, das ohnehin
 * herausfaellt), ihre Werte werden aber in `buildPreviousValues` bewusst durch
 * `buildKilnAnnualPreviousValues`/`buildKilnMeasurementRangeValues` UEBERSCHRIEBEN -- die
 * generische `formatTonnage`-Formatierung waere fuer Ofen-Konzentrationswerte falsch (Pitfall 1,
 * 28-RESEARCH.md).
 *
 * `alternativeFuels` (Phase 34, D-13/D-14/D-15) ist aus demselben Grund NICHT hier gelistet,
 * obwohl der ehemalige Sonstige-Brennstoffe-Block mit dem Umbau auf freie Zeilen ersatzlos
 * entfallen ist: die Zeile traegt jetzt selbst eine tragfaehige Identitaet (`rowId`),
 * ueber die `remapAlternativeFuelPositions` unten die Vorjahresposition auf die aktuelle
 * umsortiert -- genau wie bei den Oefen. `primaryFuels`/`inputMaterials` bleiben aus der
 * Ausschlussliste, weil sie ohnehin nie darin standen: feste, katalogtreue Reihenfolge (siehe die
 * Array-Hydrierer in `hydrate.ts`), ihre Vorwerte laufen unveraendert ueber die generische
 * Pipeline.
 *
 * D-04: das Fehlen an der einen verbliebenen Wurzel wird nirgends erklaert -- kein Hinweistext,
 * kein deaktivierter Schalter am Einsatzstoffe-Reiter.
 */
export const PREVIOUS_VALUE_EXCLUDED_ROOTS: readonly string[] = ['otherInputMaterials'];

/**
 * Blattschluessel, die auf JEDER Tiefe uebersprungen werden -- genau einer: `id`.
 *
 * Die Hydrierer geben Datenbankeintraege samt `id` unveraendert durch (z.B. jede
 * Drehofen-Messung, jeder Sonstige-Eintrag). Eine Datensatz-Kennung ist kein Messwert und darf
 * nie als Vorwert-Text erscheinen. Bewusst KEINE Feldliste, sondern ein einzelner
 * Metadatenschluessel -- eine handgepflegte Feldliste waere der Rueckschritt, den 20-16 mit
 * `ACCIDENT_TAB_FIELDS` gerade abgeschafft hat.
 */
export const PREVIOUS_VALUE_EXCLUDED_LEAF_KEYS: readonly string[] = ['id'];

const NUMERIC_STRING = /^-?\d+(\.\d+)?$/;

// Ein Blatt wird aufgenommen, wenn sein Wert null, undefined, eine number oder ein String ist,
// der entweder leer ist oder wie eine Zahl aussieht. Freitext (Namen, Codes), Datumsstrings
// ("2026-01-01"), Booleans und HTML aus `notes` fallen strukturell heraus -- keine
// Feldtyp-Unterscheidung noetig (D-16 ist auf der Leseseite gewahrt, s.o.).
function isIncludableLeaf(value: unknown): value is string | number | null | undefined {
  if (value === null || value === undefined || typeof value === 'number') return true;
  if (typeof value === 'string') return value === '' || NUMERIC_STRING.test(value);
  return false;
}

/**
 * Klopft ein hydriertes Formularobjekt (Ergebnis eines `hydrate*` aus `hydrate.ts`, angewandt
 * auf die Vorjahresmeldung) zu Feldpfad -> formatiertem Vorwert-Text flach.
 *
 * Erzeugt exakt die Punkt-Notation, die `getPath` (`validatedFields.tsx:273-278`) beim Lesen
 * konsumiert: Arrays ueber den numerischen Index, Objekte ueber den Property-Namen.
 *
 * Die Formatierung ueber `formatTonnage` passiert BEIM BEFUELLEN, nicht beim Rendern
 * (24-RESEARCH.md § Frage 5) -- jeder Store-Eintrag ist dadurch bereits ein primitiver String,
 * und `Object.is` in `useSyncExternalStore` (`components/reports/formStores.ts`) funktioniert
 * damit wie bei den Warnungstexten. Ein leeres Vorjahresfeld ergibt dadurch '–' (D-08), eine
 * echte Null '0,00'. Es wird nicht nach `kind` des Feldes differenziert (D-14) -- auch
 * Ganzzahlfelder bekommen zwei Nachkommastellen, weil die Nicht-Eingabe-Anzeigen aus D-15 kein
 * `kind` kennen.
 */
export function flattenReportValues(hydrated: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};

  function walk(node: unknown, path: string[]): void {
    if (Array.isArray(node)) {
      node.forEach((item, idx) => walk(item, [...path, String(idx)]));
      return;
    }

    if (node !== null && typeof node === 'object') {
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        // `id` faellt auf jeder Tiefe heraus, unabhaengig vom Pfad.
        if (PREVIOUS_VALUE_EXCLUDED_LEAF_KEYS.includes(key)) continue;
        // Die drei ausgeschlossenen Wurzeln greifen nur auf der obersten Ebene -- ein
        // gleichnamiger, tiefer verschachtelter Schluessel (kaeme aktuell nicht vor) waere
        // sonst faelschlich mitbetroffen.
        if (path.length === 0 && PREVIOUS_VALUE_EXCLUDED_ROOTS.includes(key)) continue;
        walk(value, [...path, key]);
      }
      return;
    }

    // Blatt erreicht. Der Wurzelaufruf selbst (path.length === 0, hydrated ist kein Objekt) wird
    // defensiv uebersprungen -- kommt in der Praxis nicht vor, hydrate* liefert immer ein Objekt.
    if (path.length === 0 || !isIncludableLeaf(node)) return;
    result[path.join('.')] = formatTonnage(node as string | number | null | undefined);
  }

  walk(hydrated, []);
  return result;
}

/**
 * Persistierte Rechenspalten je `kind` (aus `reportTypes.ts`), die im `hydrate*`-Ergebnis
 * FEHLEN, weil dessen Whitelist nur die beschreibbaren Felder fuehrt (24-RESEARCH.md § Frage 3):
 *
 * - `formular_ab`/`formular_c`: `inlandsVersand` -- verwaistes Entity-Feld ohne UI-Bindung
 *   (18-08-Fund), server-autoritativ, taucht deshalb in `hydrateFormularAB`/`hydrateFormularC`
 *   nicht auf, wird aber an den Summenbalken angezeigt (D-15).
 * - `accident`: die acht eingefrorenen Jahresarbeitsstunden-/Unfallratenwerte der Matrix
 *   (inklusive `annualHoursCombined`, Plan 25.1-04 -- der gepoolte Durchschnitt ohne KANUE ist wie
 *   seine sieben Nachbarn eine persistierte, nicht editierbare Rechenspalte). `hoursWorkedTotal`,
 *   `accidentsLostDayTotal` und `lostWorkDaysTotal` stehen dagegen BEREITS im Ergebnis des
 *   Unfalldaten-Hydrierers (`hydrate.ts`) und gehoeren deshalb NICHT in diese Liste.
 * - `environmental`: leer -- `waterConsumption`, die vier Rohstoffsummen (Phase 31,
 *   `primaryRawMaterialsClinker`/`primaryRawMaterialsCementMilling`/
 *   `secondaryRawMaterialsClinker`/`secondaryRawMaterialsCementMilling`) UND `clinkerStockChange`
 *   (Phase 32, P-02) stehen bereits im Ergebnis des Umwelterhebungs-Hydrierers, es fehlt nichts.
 * - `formular_d`: leer -- dort ist keine Summe persistiert, die 41 Summen kommen in Plan 24-08
 *   aus `POST /api/reports/calculate` (D-11).
 */
export const PREVIOUS_DERIVED_KEYS: Record<string, readonly string[]> = {
  formular_ab: ['inlandsVersand'],
  formular_c: ['inlandsVersand'],
  accident: [
    'annualHoursWage',
    'annualHoursAdmin',
    'annualHoursTemp',
    'annualHoursTotal',
    'annualHoursCombined',
    'accidentRateOwn',
    'accidentRateIndirect',
    'accidentRateTotal',
  ],
  environmental: [],
  formular_d: [],
};

/**
 * Baut die vollstaendigen Vorwerte fuer eine Meldung eines gegebenen Typs: die Feldpfade aus
 * `flattenReportValues(hydrated)`, ueberlagert mit den persistierten Rechenspalten aus
 * `PREVIOUS_DERIVED_KEYS[kind]` (jeweils aus `raw` gelesen).
 *
 * Ein in `raw` fehlender abgeleiteter Schluessel erzeugt trotzdem einen Eintrag mit '–' -- die
 * Anzeigestelle existiert, also existiert ihre Aussage (D-08). Ein unbekannter `kind` liefert nur
 * das flache Ergebnis, ohne zu werfen.
 *
 * Importiert bewusst KEINE `hydrate*`-Funktion selbst -- der Aufrufer (Plan 24-05) hat `hydrate`
 * bereits aus `REPORT_TYPES` (`reportTypes.ts`) in der Hand. Das haelt dieses Modul frei von
 * Zyklen und macht es ohne Registry testbar.
 */
/**
 * Kontext fuer die Drehofen- UND Sekundaerbrennstoff-Vorwerte (Phase 28, D-15/D-16; Phase 34,
 * D-13/D-14/D-15), nur ausgewertet fuer `kind === 'environmental'`. `currentKilns` traegt die
 * AKTUELLE Ofenliste (fuer die Positionszuordnung ueber `plantKiln.id`), `previousYear` das
 * tatsaechliche Vorjahr fuer den "nicht gemessen"-Text (UI-SPEC Punkt 16). `currentAlternativeFuels`
 * traegt entsprechend die AKTUELLE Zeilenliste der Sekundaerbrennstoffe (Positionszuordnung ueber
 * `rowId`) -- fehlt sie, liefert `remapAlternativeFuelPositions` ein leeres Array, derselbe
 * "kein Kontext heisst kein Vorwert"-Grundsatz wie bei den Oefen.
 */
export interface KilnPreviousValuesContext {
  currentKilns: RotaryKiln[] | undefined;
  previousYear: string;
  currentAlternativeFuels?: AlternativeFuelEntry[];
}

export function buildPreviousValues(
  kind: string,
  raw: Record<string, unknown>,
  hydrated: Record<string, unknown>,
  kilnContext?: KilnPreviousValuesContext,
): Record<string, string> {
  // Phase 28 (D-15): fuer die Umwelterhebung wird `rotaryKilns` VOR dem Flatten durch die
  // umsortierten Ofenpositionen ersetzt -- ohne `kilnContext` durch ein leeres Array (kein
  // Kontext heisst: keine Ofen-Vorwerte, aber unter keinen Umstaenden positionale Werte am
  // falschen Ofen). Phase 34 (D-13/D-14/D-15): `alternativeFuels` wird an derselben Stelle,
  // nach demselben Grundsatz, durch die umsortierten Zeilenpositionen ersetzt. Fuer die vier
  // anderen Formulartypen bleibt `hydrated` unveraendert.
  let workingHydrated = hydrated;
  let remappedKilns: Record<string, unknown>[] = [];
  let remappedAlternativeFuels: Record<string, unknown>[] = [];
  const previousKilns =
    kind === 'environmental' ? ((hydrated as { rotaryKilns?: RotaryKiln[] }).rotaryKilns) : undefined;

  if (kind === 'environmental') {
    remappedKilns = remapKilnPositions(previousKilns, kilnContext?.currentKilns);
    remappedAlternativeFuels = remapAlternativeFuelPositions(
      (hydrated as { alternativeFuels?: AlternativeFuelEntry[] }).alternativeFuels,
      kilnContext?.currentAlternativeFuels,
    );
    workingHydrated = {
      ...hydrated,
      rotaryKilns: remappedKilns,
      alternativeFuels: remappedAlternativeFuels,
    };
  }

  const result = flattenReportValues(workingHydrated);
  const derivedKeys = PREVIOUS_DERIVED_KEYS[kind] ?? [];
  for (const key of derivedKeys) {
    result[key] = formatTonnage(raw[key] as string | number | null | undefined);
  }

  if (kind === 'environmental') {
    // Uebernimmt ausschliesslich die Menge (D-13) unter dem echten Feldpfad `amount` -- der
    // Zwischenschluessel `amountValue` aus `remapAlternativeFuelPositions` ist kein Formularfeld,
    // das generische Flatten oben erzeugt ihn deshalb nur als ungenutzten Nebenbefund.
    Object.assign(result, buildAlternativeFuelPreviousValues(remappedAlternativeFuels));
  }

  if (kind === 'environmental' && kilnContext) {
    // Ueberschreibt bewusst, was das generische Flatten oben bereits erzeugt hat: dessen
    // `formatTonnage` hat fuer Ofen-Konzentrationswerte die falsche Praezision (Pitfall 1,
    // 28-RESEARCH.md). Diese Reihenfolge -- NACH dem generischen Flatten -- ist Absicht, nicht
    // doppelte Arbeit: die Ofen-Funktionen unten brauchen die vollstaendigen (nicht auf die neun
    // Jahresmittelwerte reduzierten) Vorjahresoefen fuer die Spannweite (`measurements`).
    Object.assign(result, buildKilnAnnualPreviousValues(remappedKilns));
    Object.assign(
      result,
      buildKilnMeasurementRangeValues(previousKilns, kilnContext.currentKilns, kilnContext.previousYear),
    );
    // Phase 38 (D-16): Vorwerte der 39 Rechenblatt-Eingabefelder, eine Ebene tiefer als die neun
    // Jahresmittelwerte oben -- eigene Remap-Funktion (previousKiln.loadCalculation statt direkt
    // am Ofen), siehe remapKilnLoadPositions unten.
    Object.assign(result, buildKilnLoadPreviousValues(remapKilnLoadPositions(previousKilns, kilnContext.currentKilns)));
  }

  return result;
}

/* ==========================================================================================
 * Drehofen-Vorwerte (Phase 28, D-15/D-16/D-17): Ofenidentitaet ueber `plantKiln.id`.
 * ========================================================================================== */

/**
 * Ordnet die Drehoefen der VORJAHRESMELDUNG auf die Positionen der AKTUELLEN Ofenliste um
 * (D-15). Die Ofenidentitaet kommt ausschliesslich ueber `plantKiln.id` -- niemals ueber
 * `kilnName` (freier Namens-Schnappschuss) oder die Arrayposition, die sich zwischen zwei
 * Meldungen durch Stilllegung/Neuanlage verschieben kann (Pitfall 2, 28-RESEARCH.md).
 *
 * Liefert je aktueller Ofenposition entweder ein Objekt mit ausschliesslich den neun
 * `${key}Value`-Eintraegen des passenden Vorjahresofens (Schluessel aus
 * `ALL_KILN_ANNUAL_FIELD_KEYS`) oder ein leeres Objekt, wenn der aktuelle Ofen im Vorjahr keine
 * Entsprechung hat. Bewusst NICHT mitgenommen: `measurements` (die Spannweite entsteht in
 * `buildKilnMeasurementRangeValues`, keine Positionszuordnung -- D-16), `id`, `plantKiln`,
 * `kilnName`, `sortOrder`, `hclDiscontinuous`, `hfDiscontinuous` -- nichts davon ist ein Vorwert.
 */
export function remapKilnPositions(
  previousKilns: RotaryKiln[] | undefined,
  currentKilns: RotaryKiln[] | undefined,
): Record<string, unknown>[] {
  if (!currentKilns || currentKilns.length === 0) return [];

  const byPlantKilnId = new Map<number, RotaryKiln>();
  for (const kiln of previousKilns ?? []) {
    if (kiln.plantKiln?.id !== undefined && kiln.plantKiln !== null) {
      byPlantKilnId.set(kiln.plantKiln.id, kiln);
    }
  }

  return currentKilns.map((currentKiln) => {
    const plantKilnId = currentKiln.plantKiln?.id;
    const previousKiln = plantKilnId !== undefined ? byPlantKilnId.get(plantKilnId) : undefined;
    if (!previousKiln) return {};

    const values: Record<string, unknown> = {};
    for (const key of ALL_KILN_ANNUAL_FIELD_KEYS) {
      values[`${key}Value`] = (previousKiln as unknown as Record<string, unknown>)[`${key}Value`];
    }
    return values;
  });
}

/* ==========================================================================================
 * Sekundaerbrennstoff-Vorwerte (Phase 34, D-13/D-14/D-15): Zeilenidentitaet ueber `rowId`.
 * ========================================================================================== */

/**
 * Ordnet die Zeilen der VORJAHRESMELDUNG auf die Positionen der AKTUELLEN Zeilenliste um --
 * nach demselben Grundsatz wie `remapKilnPositions` oben, aber deutlich kuerzer: Zeilenidentitaet
 * ist direkt die `rowId` (kein Umweg ueber eine externe Stammdaten-ID, die Zeile traegt ihre
 * Identitaet selbst), und uebernommen wird ausschliesslich die Menge (D-13) -- alle uebrigen
 * Feldwerte einer neu angelegten Zeile sind bereits vorbelegt (D-10, Bezeichnung/Abfallschluessel/
 * Stoffeigenschaften kommen mit der Zeile selbst), ein Vorwert daneben waere die Wiederholung des
 * eigenen Feldinhalts.
 *
 * Liefert je aktueller Zeilenposition entweder `{ amountValue: <Vorjahresmenge> }` oder ein
 * leeres Objekt -- eine aktuelle Zeile ohne `rowId` (frisch angelegt, ungespeichert) oder ohne
 * Entsprechung im Vorjahr bekommt NIE den Vorwert einer fremden Zeile (ein falsch zugeordneter
 * Vorwert ist schlimmer als gar keiner, siehe Docblock oben). Bei doppelter `rowId` im Vorjahr
 * gewinnt der erste Treffer, deterministisch, ohne zu werfen.
 */
export function remapAlternativeFuelPositions(
  previousRows: AlternativeFuelEntry[] | undefined,
  currentRows: AlternativeFuelEntry[] | undefined,
): Record<string, unknown>[] {
  if (!currentRows || currentRows.length === 0) return [];

  const byRowId = new Map<string, AlternativeFuelEntry>();
  for (const row of previousRows ?? []) {
    if (row.rowId && !byRowId.has(row.rowId)) {
      byRowId.set(row.rowId, row);
    }
  }

  return currentRows.map((currentRow) => {
    const previousRow = currentRow.rowId ? byRowId.get(currentRow.rowId) : undefined;
    if (!previousRow) return {};
    return { amountValue: previousRow.amount };
  });
}

/**
 * Muenzt den Zwischenschluessel `amountValue` aus `remapAlternativeFuelPositions` auf den echten
 * Feldpfad `alternativeFuels.{index}.amount` um -- analog zu `buildKilnAnnualPreviousValues`.
 * Eine Zeile ohne Treffer (leeres Objekt) erzeugt KEINEN Eintrag, kein '–' an einer Zeile, die im
 * Vorjahr keine Entsprechung hat.
 */
export function buildAlternativeFuelPreviousValues(
  remapped: Record<string, unknown>[],
): Record<string, string> {
  const result: Record<string, string> = {};
  remapped.forEach((row, index) => {
    if (!('amountValue' in row)) return;
    result[`alternativeFuels.${index}.amount`] = formatTonnage(
      row.amountValue as string | number | null | undefined,
    );
  });
  return result;
}

/**
 * Nachkommastellen fuer einen Drehofen-Konzentrationswert, wertabhaengig (Sketch 009,
 * `digitsFor`, 28-RESEARCH.md Pitfall 1). `formatTonnage` (zwei feste Nachkommastellen) macht
 * Quecksilber- und Schwermetallwerte wie `0,000234` informationslos -- deshalb eine eigene
 * Staffel statt der generischen Vorwerte-Formatierung.
 *
 * Stufe "unter 0,0001 -> 7" (Punkt 8 vom 14.09.2026, Durchsprache `35.1-REVIEW.md`): die
 * fuenf Nachkommastellen der Stufe darunter reichten fuer Werte aus den 14 gepruesften
 * Emissionsberichten nicht -- Thallium 0,0000094 erschien als "0,00001", PCB WHO-TEQ 0,0000012
 * als "0,00000". Sieben entspricht der seit Plan 04 geltenden Spaltenskala `DECIMAL(13,7)`.
 */
export function kilnDecimalDigits(value: number): number {
  const magnitude = Math.abs(value);
  if (magnitude === 0) return 2;
  if (magnitude < 0.0001) return 7;
  if (magnitude < 0.001) return 5;
  if (magnitude < 0.01) return 4;
  if (magnitude < 1) return 3;
  return 2;
}

/**
 * Formatiert einen Drehofen-Konzentrationswert deutsch, mit `kilnDecimalDigits(reference ??
 * value)` Nachkommastellen. `reference` traegt D-20: bei einer Spanne richten sich die
 * Nachkommastellen des groesseren Werts nach dem KLEINEREN, sonst liest sich die Spanne
 * uneinheitlich zum Feldwert daneben. `null`/`undefined`/leer liefert `'–'` (D-08).
 */
export function formatKilnNumber(
  value: number | string | null | undefined,
  reference?: number | string | null,
): string {
  if (value === null || value === undefined || value === '') return '–';
  const numericValue = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numericValue)) return '–';

  const refRaw = reference ?? value;
  const numericRef = typeof refRaw === 'string' ? Number(refRaw) : refRaw;
  const digits = kilnDecimalDigits(Number.isNaN(numericRef) ? numericValue : (numericRef as number));

  return numericValue.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Baut die Vorwerte der neun Jahresmittelwerte je Ofenposition (D-15) aus dem Ergebnis von
 * `remapKilnPositions`. Erzeugt je Ofenposition und je Schluessel aus
 * `ALL_KILN_ANNUAL_FIELD_KEYS` den Eintrag `rotaryKilns.{index}.{key}Value`. Fuer ein leeres
 * Ofenobjekt (keine Vorjahresentsprechung) entsteht KEIN Eintrag -- kein `'–'` an einem Ofen, der
 * im Vorjahr gar nicht existierte, das waere eine Aussage ueber nichts.
 */
export function buildKilnAnnualPreviousValues(
  remapped: Record<string, unknown>[],
): Record<string, string> {
  const result: Record<string, string> = {};
  remapped.forEach((kiln, index) => {
    if (Object.keys(kiln).length === 0) return;
    for (const key of ALL_KILN_ANNUAL_FIELD_KEYS) {
      const value = kiln[`${key}Value`] as string | number | null | undefined;
      result[`rotaryKilns.${index}.${key}Value`] = formatKilnNumber(value);
    }
  });
  return result;
}

/* ==========================================================================================
 * Frachtenberechnung-Vorwerte (Phase 38, D-16): dieselbe plantKiln.id-Identitaet wie
 * remapKilnPositions oben, aber eine Ebene tiefer -- die 39 Eingabewerte liegen unter
 * previousKiln.loadCalculation, nicht direkt am Ofen. Eine eigene Funktion statt einer
 * Erweiterung von remapKilnPositions: diese bleibt bewusst auf die neun Jahreswerte-Schluessel
 * beschraenkt (siehe deren Docblock oben), eine Erweiterung dort waere an der falschen Stelle.
 * ========================================================================================== */

/**
 * Ordnet die Rechenblaetter der VORJAHRESMELDUNG auf die Positionen der AKTUELLEN Ofenliste um
 * (D-16), exakt nach demselben `plantKiln.id`-Muster wie `remapKilnPositions` oben.
 *
 * Liefert je aktueller Ofenposition entweder ein Objekt mit den 91 Eingabewerten aus
 * `previousKiln.loadCalculation` (sechs Betriebsdaten aus `ALL_KILN_LOAD_OPERATING_KEYS`, 33
 * `<key>Value`-Konzentrationen aus `KILN_LOAD_COMPONENT_KEYS`, seit Phase 40 zusaetzlich 52
 * `<key>MassFlowCompound`/`<key>MassFlowDirect`-Massenstromfelder aus
 * `ALL_KILN_LOAD_MASS_FLOW_KEYS`, D-06/D-16) oder ein leeres Objekt -- kein Vorjahresofen, kein
 * gespeichertes Rechenblatt (`loadCalculation` ist `null`/`undefined`) oder kein Treffer ueber
 * `plantKiln.id` ergeben alle dasselbe leere Objekt.
 *
 * Ausdruecklich NICHT mitgenommen: `weightedFlow`, `totalOperatingHours` und `loads` -- diese
 * drei sind abgeleitete Werte, keine Eingaben; bei einer Formelaenderung waeren alte
 * Vorjahresfrachten ohnehin nicht mehr mit den aktuell gerechneten vergleichbar (D-16 nennt
 * ausdruecklich nur Betriebsdaten und Konzentrationen als Vorwerte). Ebenfalls NICHT mitgenommen
 * (Phase 40): `calculationBasis` -- die Auswahl Konzentration/Massenstrom folgt D-10 (der
 * Voreinstellung des aktuellen Ofens), nicht dem Vorjahr -- sowie `massFlowCoverage`/
 * `massFlowOrigin`, beide abgeleitet wie `loads` oben.
 */
export function remapKilnLoadPositions(
  previousKilns: RotaryKiln[] | undefined,
  currentKilns: RotaryKiln[] | undefined,
): Record<string, unknown>[] {
  if (!currentKilns || currentKilns.length === 0) return [];

  const byPlantKilnId = new Map<number, RotaryKiln>();
  for (const kiln of previousKilns ?? []) {
    if (kiln.plantKiln?.id !== undefined && kiln.plantKiln !== null) {
      byPlantKilnId.set(kiln.plantKiln.id, kiln);
    }
  }

  return currentKilns.map((currentKiln) => {
    const plantKilnId = currentKiln.plantKiln?.id;
    const previousKiln = plantKilnId !== undefined ? byPlantKilnId.get(plantKilnId) : undefined;
    const loadCalculation = previousKiln?.loadCalculation;
    if (!loadCalculation) return {};

    const loadValues = loadCalculation as unknown as Record<string, unknown>;
    const values: Record<string, unknown> = {};
    for (const key of ALL_KILN_LOAD_OPERATING_KEYS) {
      values[key] = loadValues[key];
    }
    for (const key of KILN_LOAD_COMPONENT_KEYS) {
      values[`${key}Value`] = loadValues[`${key}Value`];
    }
    // Phase 40 (D-06/D-16): dieselbe Uebernahme wie bei den 33 Konzentrationen oben, fuer die 52
    // Massenstrom-Mittelwerte. calculationBasis bewusst NICHT mitgenommen (siehe Docblock oben).
    for (const key of ALL_KILN_LOAD_MASS_FLOW_KEYS) {
      values[key] = loadValues[key];
    }
    return values;
  });
}

/**
 * Baut die Vorwerte der Eingabefelder des Rechenblatts je Ofenposition (D-16, erweitert Phase 40
 * D-06) aus dem Ergebnis von `remapKilnLoadPositions`. Erzeugt je Ofenposition die Schluessel
 * `rotaryKilns.{index}.loadCalculation.{feld}` -- exakt die Pfade, die
 * `KilnLoadCalculationForm.tsx` ueber `ValidatedNumberInput` liest.
 *
 * Formatierung: die 33 Konzentrationen UND die 52 Massenstrom-Mittelwerte ueber `formatKilnNumber`
 * (dieselbe wertabhaengige Nachkommastellen-Staffel wie die uebrigen Ofen-Konzentrationswerte --
 * die Massenstromfelder tragen dieselbe Praezision, 40-01), `compoundFlow`/`directFlow`/
 * `compoundHours`/`directHours` ueber `formatOperatingNumber(value, 0)`, `compoundOxygen`/
 * `directOxygen` ueber `formatOperatingNumber(value, 1)`. `calculationBasis` bekommt bewusst
 * keinen Eintrag (D-10, siehe Docblock von `remapKilnLoadPositions`).
 *
 * Fuer ein leeres Ofenobjekt (keine Vorjahresentsprechung oder kein gespeichertes Rechenblatt)
 * entsteht KEIN Eintrag -- kein '–' an einem Ofen, an dem es im Vorjahr keine Frachtenberechnung
 * gab, das waere eine Aussage ueber nichts (Wortlaut aus `buildKilnAnnualPreviousValues` oben
 * uebernommen).
 */
/**
 * Die sechs Betriebsdatenfelder mit fester Nachkommastellenzahl, aber demselben Leerzeichen wie
 * alle anderen Vorwerte dieses Moduls: `'–'` (D-08), nicht der Bindestrich `'-'`, den der
 * geteilte `formatNumber()` bei leerem Wert liefert. Ohne diese Klammer standen in derselben
 * Vorwertspalte eines Ofens zwei verschiedene Striche nebeneinander -- Bindestrich unter den
 * Betriebsdaten, Gedankenstrich unter den 33 Konzentrationen (WR-02 aus 38-REVIEW.md).
 * `formatKilnNumber()` kommt hier nicht in Frage: es leitet die Nachkommastellen aus dem Betrag
 * ab, die Betriebsdaten brauchen aber feste 0 bzw. 1.
 *
 * Das dritte Zeichen im Rechenblatt, der Geviertstrich `'—'` in `kilnLoadFormat.ts`, bleibt
 * bewusst stehen: er gehoert zu den gerechneten Zellen des Blatts und folgt dort dem Sketch 021,
 * der durchgehend `'—'` setzt. Vorwertzeile und Rechenzelle sind zwei Kontexte, nicht einer.
 */
function formatOperatingNumber(
  value: string | number | null | undefined,
  decimals: number,
): string {
  if (value === null || value === undefined || value === '') return '–';
  const numericValue = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numericValue)) return '–';
  return formatNumber(numericValue, decimals);
}

export function buildKilnLoadPreviousValues(
  remapped: Record<string, unknown>[],
): Record<string, string> {
  const result: Record<string, string> = {};
  const oneDecimalKeys = new Set(['compoundOxygen', 'directOxygen']);

  remapped.forEach((kiln, index) => {
    if (Object.keys(kiln).length === 0) return;

    for (const key of ALL_KILN_LOAD_OPERATING_KEYS) {
      const value = kiln[key] as string | number | null | undefined;
      const decimals = oneDecimalKeys.has(key) ? 1 : 0;
      result[`rotaryKilns.${index}.loadCalculation.${key}`] = formatOperatingNumber(value, decimals);
    }
    for (const key of KILN_LOAD_COMPONENT_KEYS) {
      const value = kiln[`${key}Value`] as string | number | null | undefined;
      result[`rotaryKilns.${index}.loadCalculation.${key}Value`] = formatKilnNumber(value);
    }
    // Phase 40 (D-06/D-16): dieselbe Formatierung wie die 33 Konzentrationen oben, fuer die 52
    // Massenstrom-Mittelwerte.
    for (const key of ALL_KILN_LOAD_MASS_FLOW_KEYS) {
      const value = kiln[key] as string | number | null | undefined;
      result[`rotaryKilns.${index}.loadCalculation.${key}`] = formatKilnNumber(value);
    }
  });

  return result;
}

/** `1 Messung` bzw. `{n} Messungen` -- exakter Wortlaut aus `sketch009.html:nMeas`. */
function measurementCountLabel(n: number): string {
  return n === 1 ? '1 Messung' : `${n} Messungen`;
}

/**
 * Spannweitentext eines Merkmals ueber die Vorjahresmessungen eines Ofens (D-16/D-20), strikt
 * nach UI-SPEC Punkt 16 / `sketch009.html:spanText`. `values`/`allBelow` sammeln sich aus den
 * Vorjahresmessungen, die weder `null` noch leer sind; `*BelowLimit`-Felder selbst bekommen nie
 * einen eigenen Eintrag (sie sind keine Zahlenfelder).
 */
function kilnMeasurementRangeText(
  previousMeasurements: RotaryKilnMeasurement[],
  key: string,
  previousYear: string,
): string {
  const valueKey = `${key}Value`;
  const belowKey = `${key}BelowLimit`;

  const values: number[] = [];
  let allBelow = true;
  for (const measurement of previousMeasurements) {
    const raw = (measurement as unknown as Record<string, unknown>)[valueKey];
    if (raw === null || raw === undefined || raw === '') continue;
    const numeric = typeof raw === 'string' ? Number(raw) : (raw as number);
    if (Number.isNaN(numeric)) continue;

    values.push(numeric);
    if (!(measurement as unknown as Record<string, unknown>)[belowKey]) allBelow = false;
  }

  const n = values.length;
  if (n === 0) return `${previousYear} nicht gemessen`;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const countLabel = measurementCountLabel(n);

  if (allBelow) return `alle < ${formatKilnNumber(max)} · ${countLabel}`;
  if (min === max) return `${formatKilnNumber(min)} · ${countLabel}`;
  return `${formatKilnNumber(min)} – ${formatKilnNumber(max, min)} · ${countLabel}`;
}

/**
 * Baut die Spannweiten-Vorwerte der 26 Einzelmessungsfelder (D-16): keine Messung-zu-Messung-
 * Zuordnung, sondern je Merkmal und Ofen Minimum/Maximum/Anzahl der VORJAHRESMESSUNGEN. Die
 * Ofenidentitaet ist dieselbe wie in `remapKilnPositions` (`plantKiln.id`), hier aber auf den
 * VOLLSTAENDIGEN Ofen inklusive `measurements` angewandt.
 *
 * Der erzeugte Text wird fuer JEDE Messungsposition des AKTUELLEN Ofens unter
 * `rotaryKilns.{kilnIndex}.measurements.{measurementIndex}.{key}Value` abgelegt -- die
 * Spannweite ist eine Aussage je Merkmal und Ofen (D-16), sie steht aber unter jedem Feld, an dem
 * sie gebraucht wird, damit `PreviousValueForField` sie ohne Sonderfall abfragen kann (D-19). Hat
 * der aktuelle Ofen keine Messung, entsteht kein Eintrag (es gibt kein Feld dafuer). Ein aktueller
 * Ofen ohne Vorjahresentsprechung erzeugt ausschliesslich "nicht gemessen"-Eintraege.
 */
export function buildKilnMeasurementRangeValues(
  previousKilns: RotaryKiln[] | undefined,
  currentKilns: RotaryKiln[] | undefined,
  previousYear: string,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!currentKilns || currentKilns.length === 0) return result;

  const byPlantKilnId = new Map<number, RotaryKiln>();
  for (const kiln of previousKilns ?? []) {
    if (kiln.plantKiln?.id !== undefined && kiln.plantKiln !== null) {
      byPlantKilnId.set(kiln.plantKiln.id, kiln);
    }
  }

  currentKilns.forEach((currentKiln, kilnIndex) => {
    const currentMeasurements = currentKiln.measurements ?? [];
    if (currentMeasurements.length === 0) return;

    const plantKilnId = currentKiln.plantKiln?.id;
    const previousKiln = plantKilnId !== undefined ? byPlantKilnId.get(plantKilnId) : undefined;
    const previousMeasurements = previousKiln?.measurements ?? [];

    for (const key of ALL_EMISSION_FIELD_KEYS) {
      const text = kilnMeasurementRangeText(previousMeasurements, key, previousYear);
      currentMeasurements.forEach((_, measurementIndex) => {
        result[`rotaryKilns.${kilnIndex}.measurements.${measurementIndex}.${key}Value`] = text;
      });
    }
  });

  return result;
}

/**
 * Ausloeser fuer die Neuberechnung der Ofen-Vorwerte im Loader (Plan 28-05 Task 3): eine
 * Zeichenkette aus `plantKiln.id` und der Anzahl der Messungen je Ofen der AKTUELLEN Meldung.
 * Aendert sich nur beim Anlegen/Entfernen einer Messung oder eines Ofens, nicht beim Tippen in
 * ein Feld (T-28-15: die Berechnung laeuft ueber wenige Dutzend Werte, kein Performance-Risiko).
 * Ohne diesen Schluessel in der Abhaengigkeitsliste des Loader-Effekts bekaeme eine gerade neu
 * angelegte Messung ihre Spannweitenzeile erst nach einem Neuladen der Seite.
 */
export function kilnShapeKey(kilns: RotaryKiln[] | undefined): string {
  return (kilns ?? [])
    .map((kiln) => `${kiln.plantKiln?.id ?? 'x'}:${(kiln.measurements ?? []).length}`)
    .join('|');
}
